import styled from "@emotion/styled";
import { convert, type MinimalTokenInfo } from "@stepfinance/common";
import {
  darkGrey2,
  darkGrey4,
  darkOrange,
  formatBalance,
  formatPercentage,
  greenSecondary,
  greenTag,
  lightGrey2,
  TagInfo,
  TokenTag,
  yellow,
} from "@stepfinance/react";
import type { FC, ReactNode } from "react";
import { useState } from "react";

import { ReactComponent as SuccessIcon } from "../../assets/notifications/successIcon.svg";
import { ReactComponent as WarnIcon } from "../../assets/notifications/warnIcon.svg";
import { ReactComponent as SwitchArrows } from "../../assets/switch-arrows.svg";
import { useLanguageContext } from "../../contexts/language/language";
import type { SwapEfficiencyInfoStrings } from "../../contexts/language/languageTypes";
import type { JupiterRoute } from "../../utils/jupiter/api";
import { PRICE_IMPACT_BANNER_WARNING_THRESHOLD } from "../../utils/pools/utils";
import { formatStringWithPlaceholders } from "../../utils/utils";
import { NumberLabel } from "../shared";
import { TokenIcon } from "../TokenIcon";

type EfficiencyInfoProps = {
  route: JupiterRoute;
  slippageTolerance: number;
  marketRate: number | null;
  outPerInRate: number;
  inToken?: MinimalTokenInfo;
  outToken?: MinimalTokenInfo;
};

type RateDisplayRowProps = {
  inToken: MinimalTokenInfo;
  outToken: MinimalTokenInfo;
  outPerInRate: number;
  inPerOutRate: number;
  marketRate: number | null;
  text: SwapEfficiencyInfoStrings;
};

type MinimumReceivedRowProps = {
  minimumReceived: string;
  outToken: MinimalTokenInfo;
  text: string;
};

type PriceImpactRowProps = {
  priceImpact: number;
  exceededThreshold: boolean;
  text: SwapEfficiencyInfoStrings;
};

const formatPriceImpactPercentage = (pct: number) => {
  if (pct < 0.001) {
    return "< 0.1%";
  }
  return (pct * 100).toFixed(2) + "%";
};

export const EfficiencyInfo: FC<EfficiencyInfoProps> = ({
  route,
  slippageTolerance,
  marketRate,
  outPerInRate,
  inToken,
  outToken,
}) => {
  const {
    currentLanguageData: {
      pages: {
        swapLP: { swapEfficiencyInfo: efficiencyInfoText },
      },
    },
  } = useLanguageContext();

  const outAmount = route.outAmount;
  const possibleSlippageAmount = outAmount * slippageTolerance;
  const minimumReceived = outAmount - possibleSlippageAmount;

  const inPerOutRate = 1 / outPerInRate;
  const priceImpact = route.priceImpactPct;

  return (
    <>
      {outToken && inToken && (
        <TradeInfoContainer>
          <span className="title">{efficiencyInfoText.title}</span>
          <div className="content">
            <MinimumReceivedRow
              minimumReceived={formatBalance(convert(minimumReceived, outToken.decimals), outToken.decimals)}
              outToken={outToken}
              text={efficiencyInfoText.minimumReceived}
            />
            <RateDisplayRow
              inToken={inToken}
              outToken={outToken}
              inPerOutRate={inPerOutRate}
              outPerInRate={outPerInRate}
              marketRate={marketRate}
              text={efficiencyInfoText}
            />
            <PriceImpactRow
              exceededThreshold={priceImpact * 100 > PRICE_IMPACT_BANNER_WARNING_THRESHOLD}
              priceImpact={priceImpact}
              text={efficiencyInfoText}
            />
            <SlippageToleraceRow slippageTolerance={slippageTolerance} text={efficiencyInfoText} />
          </div>
        </TradeInfoContainer>
      )}
    </>
  );
};

const MinimumReceivedRow: FC<MinimumReceivedRowProps> = ({ minimumReceived, outToken, text }) => {
  return (
    <TradeInfoRow>
      <span className="info-text">{text}</span>
      <InfoRowDiv>
        <InfoDisplayNumber>{minimumReceived}</InfoDisplayNumber>
        <TokenTag icon={<TokenIcon size={16} mintAddress={outToken.address} />} text={outToken.symbol} />
      </InfoRowDiv>
    </TradeInfoRow>
  );
};

export const RateDisplayRow: FC<RateDisplayRowProps> = ({
  inToken,
  outToken,
  inPerOutRate,
  outPerInRate,
  marketRate,
  text,
}) => {
  const [direction, setDirection] = useState(false);

  const onSwitch = () => {
    setDirection(!direction);
  };

  const displayRate = direction ? outPerInRate : inPerOutRate;
  const displayToken = direction ? outToken : inToken;
  const otherToken = direction ? inToken : outToken;

  // % difference between market rate and this route's rate. (B - A) / B
  const outRateToCGRatio = marketRate ? (marketRate - outPerInRate) / marketRate : 0;

  const displayRatioPercentage = Math.abs(outRateToCGRatio * 100).toFixed(2);
  let fillColor: string = greenSecondary;
  let blurbText: string | ReactNode[] = formatStringWithPlaceholders(
    text.coingeckoComparisons.cheaperThan,
    displayRatioPercentage,
  );
  let icon = <SuccessIcon />;
  if (outRateToCGRatio < 0.02 && outRateToCGRatio > 0) {
    fillColor = greenSecondary;
    blurbText = formatStringWithPlaceholders(text.coingeckoComparisons.withinPriceRange, displayRatioPercentage);
  } else if (outRateToCGRatio >= 0.02 && outRateToCGRatio < 0.05) {
    fillColor = yellow;
    blurbText = formatStringWithPlaceholders(text.coingeckoComparisons.moreExpensive, displayRatioPercentage);
    icon = <WarnIcon />;
  } else if (outRateToCGRatio >= 0.05) {
    fillColor = darkOrange;
    blurbText = formatStringWithPlaceholders(text.coingeckoComparisons.moreExpensive, displayRatioPercentage);
    icon = <WarnIcon />;
  }

  return (
    <>
      <TradeInfoRow>
        <InfoRowDiv>
          {marketRate && <InfoIconContainer fillColor={fillColor}>{icon}</InfoIconContainer>}
          <span className="info-text">{text.rate}</span>
        </InfoRowDiv>
        <InfoRowDiv>
          {formatStringWithPlaceholders(
            text.amountOfTokenPerToken,
            <InfoDisplayNumber>{formatBalance(displayRate, displayToken.decimals)}</InfoDisplayNumber>,
            <TokenTag icon={<TokenIcon size={16} mintAddress={displayToken.address} />} text={displayToken.symbol} />,
            <TokenTag icon={<TokenIcon size={16} mintAddress={otherToken.address} />} text={otherToken.symbol} />,
          )}
          <SwitchArrowsContainer>
            <SwitchArrows onClick={onSwitch} width={10} height={10} />
          </SwitchArrowsContainer>
        </InfoRowDiv>
      </TradeInfoRow>
      {marketRate && <MarketRateComparisonBlurb color={fillColor}>{blurbText}</MarketRateComparisonBlurb>}
    </>
  );
};

const PriceImpactRow: FC<PriceImpactRowProps> = ({ priceImpact, exceededThreshold, text }) => {
  return (
    <TradeInfoRow>
      <InfoRowDiv>
        <InfoIconContainer fillColor={exceededThreshold ? darkOrange : undefined}>
          {exceededThreshold ? <WarnIcon /> : <SuccessIcon />}
        </InfoIconContainer>
        <span className="info-text">{text.priceImpact}</span>
      </InfoRowDiv>
      <InfoRowDiv>
        <TagInfo tagType="default" tagColorOverride={exceededThreshold ? darkOrange : greenTag}>
          <NumberLabel>{formatPriceImpactPercentage(priceImpact)}</NumberLabel>
        </TagInfo>
      </InfoRowDiv>
    </TradeInfoRow>
  );
};

const SlippageToleraceRow: FC<{ slippageTolerance: number; text: SwapEfficiencyInfoStrings }> = ({
  slippageTolerance,
  text,
}) => {
  return (
    <TradeInfoRow>
      <InfoRowDiv>
        <span className="info-text">{text.slippageTolerance}</span>
      </InfoRowDiv>
      <InfoRowDiv>
        <TagInfo tagType="default" tagColorOverride={darkGrey4}>
          <NumberLabel>{formatPercentage(slippageTolerance, { capped: false })}</NumberLabel>
        </TagInfo>
      </InfoRowDiv>
    </TradeInfoRow>
  );
};

const SwitchArrowsContainer = styled.div`
  display: flex;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
`;

const MarketRateComparisonBlurb = styled.span<{ color: string }>`
  margin-left: 10px;
  font-size: 10px;
  color: ${({ color }) => color};
`;

const InfoIconContainer = styled.div<{ fillColor?: string }>`
  display: flex;
  align-items: center;

  svg {
    path {
      ${({ fillColor }) => (fillColor ? `fill: ${fillColor};` : "")}
    }
  }
`;

export const InfoDisplayNumber = styled.span`
  color: white !important;
  font-weight: 400 !important;
  font-family: Space Mono;
`;

export const TradeInfoRow = styled.div`
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  span {
    font-weight: 700;
  }
`;

export const InfoRowDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const TradeInfoContainer = styled.div`
  background-color: ${darkGrey2};
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 10px;

  svg {
    width: 16px;
    height: 16px;
  }

  .info-text {
    color: ${lightGrey2};
  }

  .title {
    color: white;
    font-size: 14px;
    font-weight: 700;
  }

  .ant-tag {
    margin: 0;
  }

  .content {
    font-size: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 8px;
  }
`;
